import React, {useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {useIntl} from 'react-intl';
import {useHistory} from 'react-router-dom';
import Styled from 'styled-components/macro';
import {
	Breadcrumbs,
	Button,
	Card,
	Checkbox,
	FlexColumn,
	FlexRow,
	Label,
	Link,
	Text,
	TextField,
} from '@forecast-it/design-system';
import {PageHeader} from '@/web-components/core/page-header';
import {ForecastQueryRenderer} from '../../ForecastQueryRenderer';
import {clientLogoSrc} from '../../directApi';
import {hasFeatureFlag} from '../shared/util/FeatureUtil';
import {FEATURE_FLAGS} from '../../constants';
import {hasPermission} from '../shared/util/PermissionsUtil';
import {PERMISSION_TYPE} from '../../Permissions';
import Util from '../shared/util/util';
import {showChangesSavedToast} from '../shared/components/toasts/toast';
import UpdateClientPortalConfigMutation from '../../mutations/update_client_portal_config_mutation';
import UploadClientPortalLogoMutation from '../../mutations/upload_client_portal_logo_mutation';
import WorkspaceUsersSection from './WorkspaceUsersSection';

type UploadPortalLogoResponse = {
	uploadClientPortalLogo?: {clientPortalConfig?: {portalLogoUrl?: string | null} | null} | null;
} | null;

/**
 * Accepts what both server gates accept: a 3- or 6-digit hex (the graphql proxy and
 * the portal admin router expand `#abc` to `#aabbcc`), or empty for "clear it and use
 * the Forecast default".
 *
 * Validated here as well because Save batches EVERY dirty field into ONE mutation: an
 * unvalidated bad colour fails the whole save, so the sender name and flags the user
 * also edited vanish behind a generic correlation-ID error modal. Catching it in the
 * field keeps the failure local, legible, and non-destructive.
 */
const PORTAL_COLOUR_RE = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
const isValidPortalColour = (value: string): boolean => value === '' || PORTAL_COLOUR_RE.test(value);

type ClientHeaderData = {
	id: string;
	name: string;
	logoId: string | null;
	status: {label: string; standing: 'ACTIVE' | 'INACTIVE'} | null;
};

type PortalConfigData = {
	portalActive: boolean | null;
	portalLogoUrl: string | null;
	portalPrimaryColour: string | null;
	portalSenderName: string | null;
	showInvoices: boolean | null;
	allowFileUpload: boolean | null;
	showRetainer: boolean | null;
	hideTimeEntries: boolean | null;
	hideEstimates: boolean | null;
};

// Status-badge colours, mirroring the client Overview and Opportunities headers
// (active = on-track green, inactive = grey).
const COLOR_STATUS_ACTIVE = '#27bb8f';
const COLOR_STATUS_INACTIVE = '#a1a1b0';
const COLOR_TEXT_MAIN = '#232334';

const PageContent = Styled.div`
	display: flex;
	flex-direction: column;
	flex: 1 1 auto;
	min-height: 0;
	width: 100%;
`;

// Header sits above the content with the same 32px horizontal rhythm as the Overview and
// Opportunities tabs.
const HeaderBar = Styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	padding: 8px 32px 16px 32px;
`;

const TitleRow = Styled.div`
	display: flex;
	align-items: center;
	gap: 12px;
`;

const ClientLogo = Styled.img`
	width: 32px;
	height: 32px;
	border-radius: 6px;
	object-fit: cover;
	flex-shrink: 0;
`;

const StatusBadge = Styled.span<{standing: 'ACTIVE' | 'INACTIVE'}>`
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-left: auto;
	padding: 6px 12px;
	border: 1px solid ${({standing}) => (standing === 'ACTIVE' ? COLOR_STATUS_ACTIVE : COLOR_STATUS_INACTIVE)};
	border-radius: 8px;
	font-size: 14px;
	line-height: 20px;
	color: ${COLOR_TEXT_MAIN};
	background-color: #ffffff;
`;

const StatusDot = Styled.span<{standing: 'ACTIVE' | 'INACTIVE'}>`
	width: 8px;
	height: 8px;
	border-radius: 50%;
	background-color: ${({standing}) => (standing === 'ACTIVE' ? COLOR_STATUS_ACTIVE : COLOR_STATUS_INACTIVE)};
`;

const ContentArea = Styled.div`
	padding: 0 32px 32px 32px;
	flex: 1 1 auto;
	min-height: 0;
	overflow-y: auto;
`;

// The portal logo preview is capped rather than stretched: firms upload wordmarks of wildly
// different aspect ratios and the picker is a preview, not the portal's own rendering.
const PortalLogoPreview = Styled.img`
	max-height: 48px;
	max-width: 200px;
	object-fit: contain;
`;

const HiddenFileInput = Styled.input`
	display: none;
`;

type WorkspaceHeaderProps = {
	client: ClientHeaderData | null;
	clientId: string;
};

// Breadcrumb (All Clients › <client> › Workspace) + client logo/name/status, matching the
// client-detail page chrome. The final crumb is the current page, so it is plain text; the client
// name links back to the client Overview.
const WorkspaceHeader = ({client, clientId}: WorkspaceHeaderProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const history = useHistory();
	const clientsListPath = hasFeatureFlag(FEATURE_FLAGS.NEW_CLIENTS_FEATURE) ? '/clients' : '/admin/clients';
	const clientName = client?.name ?? '';
	const status = client?.status ?? null;
	// Keep the href for accessibility / open-in-new-tab, but do a client-side push on a plain
	// left-click so the crumbs navigate within the SPA instead of triggering a full page reload.
	const spaNavigate = (path: string) => (event: React.MouseEvent) => {
		if (event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey) {
			event.preventDefault();
			history.push(path);
		}
	};
	return (
		<HeaderBar>
			<Breadcrumbs>
				<Breadcrumbs.Item>
					<Link href={clientsListPath} onClick={spaNavigate(clientsListPath)}>
						{formatMessage({id: 'company_view_page.breadcrumb_all_clients'})}
					</Link>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					<Link href={`/clients/${clientId}`} onClick={spaNavigate(`/clients/${clientId}`)}>
						{clientName}
					</Link>
				</Breadcrumbs.Item>
				<Breadcrumbs.Item>
					<span>{formatMessage({id: 'company_view_page.nav_workspace'})}</span>
				</Breadcrumbs.Item>
			</Breadcrumbs>
			<TitleRow data-cy="company-workspace-title">
				{client?.logoId ? (
					<ClientLogo crossOrigin="use-credentials" data-private src={clientLogoSrc(client.logoId)} alt="" />
				) : null}
				<PageHeader>{clientName}</PageHeader>
				{status ? (
					<StatusBadge standing={status.standing} data-cy="company-workspace-client-status">
						<StatusDot standing={status.standing} />
						{status.label}
					</StatusBadge>
				) : null}
			</TitleRow>
		</HeaderBar>
	);
};

type WorkspaceSettingsProps = {
	clientId: string;
	clientName: string;
	portalConfig: PortalConfigData;
	/** When the workspace is inactive the fields render but cannot be edited or saved. */
	disabled: boolean;
};

// Branding + feature flags for this client's workspace. Edited locally and committed on an explicit
// Save, mirroring the firm-wide Client Workspace settings page. The logo is the exception: it is a
// binary mutation of its own and uploads the moment a file is picked, independent of Save.
const WorkspaceSettings = ({clientId, clientName, portalConfig, disabled}: WorkspaceSettingsProps): JSX.Element => {
	const {formatMessage} = useIntl();
	const portalLogoInputRef = useRef<HTMLInputElement>(null);

	// allowFileUpload is ON-by-default in the portal's effective-permissions view, so an
	// unconfigured client must render CHECKED — otherwise the box reads "uploads off" while
	// uploads are actually permitted. Derived once and reused by the dirty check below: if the
	// seed and the baseline disagree, the form is dirty on open (or never dirty when toggled back).
	const portalAllowFileUploadInitial = portalConfig.allowFileUpload ?? true;

	// show_retainer is ON-by-default in the portal's effective-permissions view, so an
	// absent flag row must read as checked — and the dirty-check must compare against
	// that same default or tick-then-untick writes nothing.
	const portalShowRetainerInitial = portalConfig.showRetainer ?? true;

	const [portalPrimaryColour, setPortalPrimaryColour] = useState<string>(portalConfig.portalPrimaryColour || '');
	// Set on a rejected Save, cleared as soon as the user edits the field again, so the
	// message appears when they act on it and disappears when they start fixing it.
	const [portalPrimaryColourError, setPortalPrimaryColourError] = useState<boolean>(false);
	const [portalSenderName, setPortalSenderName] = useState<string>(portalConfig.portalSenderName || '');
	const [portalShowInvoices, setPortalShowInvoices] = useState<boolean>(!!portalConfig.showInvoices);
	const [portalAllowFileUpload, setPortalAllowFileUpload] = useState<boolean>(portalAllowFileUploadInitial);
	const [portalShowRetainer, setPortalShowRetainer] = useState<boolean>(portalShowRetainerInitial);
	const [portalHideTimeEntries, setPortalHideTimeEntries] = useState<boolean>(!!portalConfig.hideTimeEntries);
	const [portalHideEstimates, setPortalHideEstimates] = useState<boolean>(!!portalConfig.hideEstimates);
	// Mirrors portalConfig.portalLogoUrl, re-pointed on a successful upload.
	const [portalLogoUrl, setPortalLogoUrl] = useState<string | null>(portalConfig.portalLogoUrl ?? null);

	const onClickChangePortalLogo = () => portalLogoInputRef.current?.click();

	const onPortalLogoSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
		const file = e.target.files && e.target.files[0];
		// Clear the input so re-picking the same file still fires a change event — otherwise
		// retrying a rejected upload is silently inert.
		e.target.value = '';
		if (!file) return;
		// File rides as an uploadable — not a GraphQL variable — so the mutation reads it off the
		// multipart request body rather than from args.
		Util.CommitMutation(
			UploadClientPortalLogoMutation,
			{clientOrgId: String(clientId)},
			(response: UploadPortalLogoResponse) => {
				// ClientPortalConfig carries no id, so Relay cannot normalize the payload back
				// onto client.portalConfig. Track the fresh URL locally so the preview updates
				// without a page reload.
				const url = response?.uploadClientPortalLogo?.clientPortalConfig?.portalLogoUrl;
				if (url) setPortalLogoUrl(url);
				showChangesSavedToast(formatMessage);
			},
			true,
			{file}
		);
	};

	const onColourChange = (value: string) => {
		setPortalPrimaryColour(value);
		if (portalPrimaryColourError) setPortalPrimaryColourError(false);
	};

	const onClickSave = () => {
		// Refuse the save rather than letting one bad colour take the whole batched
		// mutation (and every other edit in it) down with a generic error modal.
		if (!isValidPortalColour(portalPrimaryColour)) {
			setPortalPrimaryColourError(true);
			return;
		}
		// Only the fields the user actually touched are sent. An omitted field is left untouched by
		// the portal, so a narrow payload avoids re-asserting values this form never owned — and an
		// entirely clean form must not write a config row for a client the portal has never seen.
		const changed: Record<string, string | boolean | null> = {};
		if ((portalPrimaryColour || '') !== (portalConfig.portalPrimaryColour || '')) {
			changed.portalPrimaryColour = portalPrimaryColour || null;
		}
		if ((portalSenderName || '') !== (portalConfig.portalSenderName || '')) {
			changed.portalSenderName = portalSenderName || null;
		}
		if (portalShowInvoices !== !!portalConfig.showInvoices) changed.showInvoices = portalShowInvoices;
		if (portalAllowFileUpload !== portalAllowFileUploadInitial) changed.allowFileUpload = portalAllowFileUpload;
		if (portalShowRetainer !== portalShowRetainerInitial) changed.showRetainer = portalShowRetainer;
		if (portalHideTimeEntries !== !!portalConfig.hideTimeEntries) changed.hideTimeEntries = portalHideTimeEntries;
		if (portalHideEstimates !== !!portalConfig.hideEstimates) changed.hideEstimates = portalHideEstimates;

		if (Object.keys(changed).length === 0) return;

		Util.CommitMutation(
			UpdateClientPortalConfigMutation,
			{
				clientOrgId: String(clientId),
				// Forwarded so the portal can name the client org it provisions on a first-touch
				// write; it seeds a new row only and never renames an org the portal already knows.
				clientName,
				...changed,
			},
			() => showChangesSavedToast(formatMessage)
		);
	};

	return (
		<FlexColumn gap={'l'} data-cy="workspace-settings">
			<FlexColumn gap={'s'}>
				<Label>{formatMessage({id: 'client_portal.logo', defaultMessage: 'Portal logo'})}</Label>
				{portalLogoUrl ? (
					// The portal serves this from object storage as a pre-signed URL, so the browser
					// must NOT attach credentials (the signature is the auth, and the bucket sends no
					// CORS headers). data-private still keeps the src out of LogRocket recordings.
					<PortalLogoPreview
						data-testid="portal-logo-preview"
						src={portalLogoUrl}
						alt={formatMessage({id: 'client_portal.logo', defaultMessage: 'Portal logo'})}
						data-private
					/>
				) : (
					<Text>
						{formatMessage({
							id: 'client_portal.logo_default',
							defaultMessage: 'Using the Forecast default logo',
						})}
					</Text>
				)}
				<HiddenFileInput
					data-testid="portal-logo-input"
					type="file"
					ref={portalLogoInputRef}
					accept="image/png,image/jpeg,image/webp"
					onChange={onPortalLogoSelected}
					disabled={disabled}
					aria-label={formatMessage({id: 'client_portal.logo', defaultMessage: 'Portal logo'})}
				/>
				<FlexRow>
					<Button emphasis="low" onPress={onClickChangePortalLogo} disabled={disabled}>
						{formatMessage({id: portalLogoUrl ? 'clients.update-logo' : 'clients.upload-logo'})}
					</Button>
				</FlexRow>
			</FlexColumn>

			<TextField
				label={formatMessage({id: 'client_portal.primary_colour', defaultMessage: 'Primary colour (hex)'})}
				value={portalPrimaryColour}
				onValueChange={onColourChange}
				disabled={disabled}
				hasError={portalPrimaryColourError}
				errorText={
					portalPrimaryColourError
						? formatMessage({
								id: 'client_portal.primary_colour_invalid',
								defaultMessage: 'Enter a hex colour like #0055FF',
						  })
						: undefined
				}
				size="large"
				width="flex"
			/>

			<TextField
				label={formatMessage({id: 'client_portal.sender_name', defaultMessage: 'Email sender name'})}
				value={portalSenderName}
				onValueChange={setPortalSenderName}
				disabled={disabled}
				size="large"
				width="flex"
			/>

			<Checkbox
				checked={portalShowInvoices}
				onChange={() => setPortalShowInvoices(p => !p)}
				disabled={disabled}
				labelText={formatMessage({id: 'client_portal.flag_show_invoices', defaultMessage: 'Show invoices'})}
			/>
			<Checkbox
				checked={portalShowRetainer}
				onChange={() => setPortalShowRetainer(p => !p)}
				disabled={disabled}
				labelText={formatMessage({id: 'client_portal.flag_show_retainer', defaultMessage: 'Show retainer usage'})}
			/>
			<Checkbox
				checked={portalAllowFileUpload}
				onChange={() => setPortalAllowFileUpload(p => !p)}
				disabled={disabled}
				labelText={formatMessage({id: 'client_portal.flag_allow_file_upload', defaultMessage: 'Allow file upload'})}
			/>
			<Checkbox
				checked={portalHideTimeEntries}
				onChange={() => setPortalHideTimeEntries(p => !p)}
				disabled={disabled}
				labelText={formatMessage({id: 'client_portal.flag_hide_time_entries', defaultMessage: 'Hide time entries'})}
			/>
			<Checkbox
				checked={portalHideEstimates}
				onChange={() => setPortalHideEstimates(p => !p)}
				disabled={disabled}
				labelText={formatMessage({id: 'client_portal.flag_hide_estimates', defaultMessage: 'Hide estimates'})}
			/>

			<FlexRow>
				<Button onPress={onClickSave} disabled={disabled} data-cy="workspace-save">
					{formatMessage({id: 'common.save', defaultMessage: 'Save'})}
				</Button>
			</FlexRow>
		</FlexColumn>
	);
};

type RawPortalUser = {
	readonly id: string | null;
	readonly email: string | null;
	readonly roleKey: string | null;
	readonly status: string | null;
	readonly scopeLevel: string | null;
	readonly scopeProgrammeIds: ReadonlyArray<string | null> | null;
	readonly scopeProjectIds: ReadonlyArray<string | null> | null;
};

type RawHierarchyNode = {
	readonly id: string | null;
	readonly name: string | null;
	readonly programmeId?: string | null;
};

type ClientUsersData = {
	readonly portalUsers?: ReadonlyArray<RawPortalUser | null> | null;
	readonly clientPortalHierarchy?: {
		readonly programmes: ReadonlyArray<RawHierarchyNode | null> | null;
		readonly projects: ReadonlyArray<RawHierarchyNode | null> | null;
	} | null;
};

type WorkspaceContentProps = {
	clientId: string;
	client: (ClientHeaderData & {portalConfig: PortalConfigData | null} & ClientUsersData) | null;
};

const WorkspaceContent = ({clientId, client}: WorkspaceContentProps): JSX.Element => {
	const {formatMessage} = useIntl();
	// Presentation only — the write is gated server-side on the same permission. Without it the
	// portal fields resolve to null rather than erroring, so an unauthorized viewer would otherwise
	// be shown an empty form they cannot save.
	const canManageSettings = hasPermission(PERMISSION_TYPE.MANAGE_ACCOUNT_SETTINGS);
	const portalConfig = client?.portalConfig ?? null;

	if (!canManageSettings) {
		return (
			<ContentArea>
				<Text data-cy="workspace-no-permission">
					{formatMessage({
						id: 'client_portal.insufficient_permission',
						defaultMessage: 'You do not have permission to manage this workspace.',
					})}
				</Text>
			</ContentArea>
		);
	}

	// The workspace is activated from the Edit Client dialog, not here, so an inactive workspace
	// shows its settings read-only rather than hiding them: an admin can see what is configured
	// before deciding to turn it on.
	const isActive = !!portalConfig?.portalActive;

	return (
		<ContentArea>
			<FlexColumn gap={'l'}>
				<Card>
					<Card.Title>
						{formatMessage({id: 'client_portal.section_title', defaultMessage: 'Client Workspace'})}
					</Card.Title>
					<Card.Body>
						<FlexColumn gap={'l'}>
							{!isActive && (
								<Text color={'secondary'} data-cy="workspace-inactive-prompt">
									{formatMessage({
										id: 'client_portal.activate_prompt_workspace_tab',
										defaultMessage:
											'This workspace is inactive. Activate it in the Edit Client dialog to apply these settings.',
									})}
								</Text>
							)}
							<WorkspaceSettings
								clientId={clientId}
								clientName={client?.name ?? ''}
								portalConfig={
									portalConfig ?? {
										portalActive: false,
										portalLogoUrl: null,
										portalPrimaryColour: null,
										portalSenderName: null,
										showInvoices: null,
										allowFileUpload: null,
										showRetainer: null,
										hideTimeEntries: null,
										hideEstimates: null,
									}
								}
								disabled={!isActive}
							/>
						</FlexColumn>
					</Card.Body>
				</Card>

				{/* Portal-user provisioning / invitation / deactivation for this client. Only shown when
				    the workspace is active — activation happens in the Edit Client dialog, and this page
				    refetches on mount, so the persisted value is the truth. No live-toggle prop passed:
				    WorkspaceUsersSection gates itself on the persisted portalConfig.portalActive. */}
				{client && !!client.portalConfig?.portalActive && (
					<Card>
						<Card.Title>
							{formatMessage({id: 'client_workspace.users.section_title', defaultMessage: 'Users'})}
						</Card.Title>
						<Card.Body>
							<WorkspaceUsersSection client={client} />
						</Card.Body>
					</Card>
				)}
			</FlexColumn>
		</ContentArea>
	);
};

export const CompanyViewWorkspaceQuery = graphql`
	query CompanyViewWorkspace_Query($clientId: String) {
		viewer {
			actualPersonId
			company {
				id
				client(id: $clientId) {
					id
					name
					logoId
					status {
						label
						standing
					}
					portalConfig {
						portalActive
						portalLogoUrl
						portalPrimaryColour
						portalSenderName
						showInvoices
						allowFileUpload
						showRetainer
						hideTimeEntries
						hideEstimates
					}
					portalUsers {
						id
						email
						roleKey
						status
						scopeLevel
						scopeProgrammeIds
						scopeProjectIds
					}
					clientPortalHierarchy {
						programmes {
							id
							name
						}
						projects {
							id
							name
							programmeId
						}
					}
				}
			}
		}
	}
`;

type Props = {
	clientId: string;
};

// The client-detail Workspace tab: this client's Customer Portal configuration. Owns its own query
// boundary. The default store-and-network policy matters here — activating the workspace happens in
// the Edit Client dialog and its mutation payload cannot be normalized onto client.portalConfig, so
// the tab has to refetch on mount to see a just-activated workspace.
export const CompanyViewWorkspace = ({clientId}: Props): JSX.Element => {
	return (
		<ForecastQueryRenderer
			key={'query-render-company-workspace-' + clientId}
			query={CompanyViewWorkspaceQuery}
			variables={{clientId}}
			render={relayProps => {
				const client = relayProps?.viewer?.company?.client ?? null;
				return (
					<PageContent>
						<WorkspaceHeader client={client} clientId={clientId} />
						<WorkspaceContent clientId={clientId} client={client} />
					</PageContent>
				);
			}}
		/>
	);
};

export default CompanyViewWorkspace;
